import { useEffect, useRef, useCallback } from 'react';
import { toast } from 'sonner';
import api from '../lib/api';

/**
 * Real-time alert stream. The hook mints its own short-lived WS ticket from
 * the cookie session — callers only need to pass `enabled` (e.g. auth state).
 *
 * Resilience: exponential backoff (5s → 10s → 20s … capped at 5 min) with a
 * circuit-breaker after 6 consecutive failures so production logs don't get
 * flooded when the WS layer is unavailable (ingress misconfig, proxy issue,
 * etc.). A successful connection resets the counter.
 */
const MAX_RETRIES = 6;
const MIN_DELAY_MS = 5000;
const MAX_DELAY_MS = 5 * 60 * 1000;

export function useRealtimeAlerts(enabled) {
  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);
  const failureCount = useRef(0);
  const givenUp = useRef(false);

  const connect = useCallback(async () => {
    if (!enabled || givenUp.current) return;

    let token = '';
    try {
      const { data } = await api.get('/api/auth/ws-ticket');
      token = data?.token || '';
    } catch (err) {
      // Silent — ws-ticket endpoint failure is non-fatal, user just won't get realtime alerts
      return;
    }
    if (!token) return;

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = new URL(process.env.REACT_APP_BACKEND_URL).host;
    const url = `${protocol}//${host}/api/ws/notifications?token=${token}`;

    try {
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        failureCount.current = 0;  // success resets the circuit breaker
        const pingInterval = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' }));
          } else {
            clearInterval(pingInterval);
          }
        }, 30000);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'sla_breach') {
            toast.error(`SLA Breach: ${data.title || 'Ticket'} (${data.priority})`, {
              description: 'Immediate attention required',
              duration: 10000,
            });
          } else if (data.type === 'alert') {
            const alert = data.alert || {};
            const toastFn = alert.type === 'critical' ? toast.error : alert.type === 'warning' ? toast.warning : toast.info;
            toastFn(alert.message || 'New alert', { duration: 8000 });
          } else if (data.type === 'new_ticket_critical') {
            toast.error(`New Critical Ticket: ${data.title}`, { duration: 8000 });
          }
        } catch (err) {
          /* malformed ws payload — ignore */
        }
      };

      ws.onclose = () => {
        failureCount.current += 1;
        if (failureCount.current >= MAX_RETRIES) {
          givenUp.current = true;  // stop hammering if the WS isn't reachable
          return;
        }
        // Exponential backoff: 5s, 10s, 20s, 40s, 80s, 160s (capped at 5 min)
        const delay = Math.min(
          MIN_DELAY_MS * Math.pow(2, failureCount.current - 1),
          MAX_DELAY_MS,
        );
        reconnectTimer.current = setTimeout(connect, delay);
      };

      ws.onerror = () => { /* handled via onclose retry */ };
    } catch (err) {
      failureCount.current += 1;
    }
  }, [enabled]);

  useEffect(() => {
    failureCount.current = 0;
    givenUp.current = false;
    connect();
    return () => {
      if (wsRef.current) wsRef.current.close();
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
    };
  }, [connect]);
}
